import {
  Box,
  Button,
  createStyles,
  Select,
  Text,
  TextInput,
} from "@mantine/core";
import { IconSearch, IconChevronDown } from "@tabler/icons-react";
import React, { useCallback, useEffect, useState } from "react";
import { getProviderServices } from "../../services/servicecode";
import { useSelector } from "react-redux";
import TableLoader from "../TableLoader";
import { debounce, showError } from "../../utils";
import store from "../../store";
import { setAllProviderServices } from "../../store/reducers/servicecode";
import ProviderServicesLookupModal from "./providerServicesLookupModal";
import swal from "sweetalert";

const bgColor = "var(--app-surface)";

const useStyles = createStyles(() => ({
  container: {
    padding: "5px 0px 10px 0px",
    background: "var(--app-surface)",
  },

  fieldLabel: {
    fontSize: "12.5px",
    fontWeight: 600,
    color: "var(--app-text)",
    marginBottom: "6px",
    display: "block",
  },

  fieldWrap: {
    display: "flex",
    flexDirection: "column",
  },

  inputBox: {
    width: "100%",
    height: "42px",
    padding: "0 12px",
    borderRadius: "8px",
    border: "1px solid var(--app-border)",
    background: bgColor,
    display: "flex",
    alignItems: "center",
    "& input": {
      border: "none",
      background: "transparent",
      paddingInline: 0,
      fontSize: "13px",
      color: "var(--app-text)",
      height: "100%",
      width: "100%",
    },
  },

  selectBox: {
    width: "100%",
    height: "42px",
    padding: "4px 12px",
    borderRadius: "8px",
    border: "1px solid var(--app-border)",
    background: bgColor,
    display: "flex",
    alignItems: "center",
    "& input": {
      border: "none",
      background: "transparent",
      paddingInline: 0,
      fontSize: "13px",
      color: "var(--app-text)",
    },
  },

  formGrid2: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "12px",
    marginTop: "16px",
  },

  addBtn: {
    width: "100%",
    height: "40px",
    background: "var(--app-primary)",
    color: "var(--app-on-primary)",
    border: "none",
    borderRadius: "8px",
    fontSize: "12.5px",
    fontWeight: 600,
    cursor: "pointer",
    marginTop: "16px",
    marginBottom: "20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    "&:hover": {
      background: "var(--app-primary)",
    },
  },

  tableWrap: {
    border: "1px solid var(--app-border)",
    borderRadius: "8px",
    overflow: "hidden",
    marginTop: "18px",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "13px",
    "& thead tr": {
      background: "var(--app-surface-2)",
    },
    "& thead th": {
      fontSize: "11px",
      fontWeight: 600,
      color: "var(--app-text-muted)",
      textTransform: "uppercase",
      letterSpacing: "0.04em",
      padding: "10px 12px",
      textAlign: "left",
      borderBottom: "1px solid var(--app-border)",
    },
    "& tbody tr": {
      borderBottom: "1px solid var(--app-border)",
      "&:last-child": {
        borderBottom: "none",
      },
    },
    "& tbody td": {
      padding: "10px 12px",
      fontSize: "13px",
      color: "var(--app-text)",
      verticalAlign: "middle",
    },
  },

  inlineSelectBox: {
    width: "140px",
    padding: "4px 8px",
    backgroundColor: bgColor,
    borderRadius: "6px",
    border: "1px solid var(--app-border)",
    "& input": {
      border: "none",
      paddingInline: 0,
      backgroundColor: "transparent",
      fontSize: "13px",
    },
  },

  deleteBtn: {
    width: "30px",
    height: "30px",
    borderRadius: "6px",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "var(--app-text-muted)",
    padding: 0,
    "&:hover": {
      background: "var(--app-tint-red)",
      color: "#B91C1C",
    },
  },
}));

const CustomTextInput = ({ ...rest }) => {
  const useLocalStyles = createStyles(() => ({
    inputBox: {
      width: "100%",
      height: "42px",
      padding: "0 12px",
      borderRadius: "8px",
      border: "1px solid var(--app-border)",
      background: bgColor,
      display: "flex",
      alignItems: "center",
      "& input": {
        border: "none",
        background: "transparent",
        paddingInline: 0,
        fontSize: "13px",
        color: "var(--app-text)",
        height: "100%",
        width: "100%",
      },
    },
  }));
  const { classes } = useLocalStyles();
  return (
    <div className={classes.inputBox}>
      <TextInput {...rest} />
    </div>
  );
};

const CustomSelectBox = ({ ...rest }) => {
  return (
    <Select
      {...rest}
      rightSection={<IconChevronDown color={"var(--app-text-muted)"} size={16} />}
      rightSectionWidth={32}
      styles={{
        rightSection: { pointerEvents: "none" },
        input: {
          height: "42px",
          border: "1px solid var(--app-border)",
          borderRadius: "8px",
          fontSize: "13px",
          paddingLeft: "12px",
          color: "var(--app-text)",
          background: bgColor,
        },
        wrapper: { width: "100%" },
      }}
    />
  );
};

function ProviderServiceSearch({
  existingServices,
  setExistingServices,
  setActiveService,
  activeService,
  isEditMode,
  setIsEditMode,
  openlookup,
  setOpenLookup,
}) {
  const { classes } = useStyles();
  const allproviderservices = useSelector(
    (state) => state?.servicecode?.allproviderservices?.allproviderservices
  );

  const [providerservices, setProviderServices] = useState({
    servicetitle: "",
    servicenumber: "",
    servicechargetype: "",
    servicerate: "",
  });
  const [services, setServices] = useState([]);
  const [search, setSearch] = useState("");
  const { loading } = useSelector((state) => state?.servicecode);

  console.log("allproviderservices", allproviderservices);

  const debouncedSearch = useCallback(
    debounce((value) => {
      value && getProviderServices(value);
    }, 700),
    []
  );

  useEffect(() => {
    !search?.trim() && store.dispatch(setAllProviderServices([]));
  }, [search]);

  useEffect(() => {
    getProviderServices("all");
  }, []);

  const handleAddService = (selectedOption) => {
    console.log("selectedOption", selectedOption);
    console.log("allproviderservices", allproviderservices);
    if (selectedOption) {
      const selectedService = allproviderservices.find(
        (service) =>
          service.name === selectedOption.name &&
          service.code === selectedOption.code
      );
      if (selectedService) {
        setProviderServices({
          servicetitle: selectedService.name,
          servicenumber: selectedService.code,
          servicechargetype: selectedService.servicechargetype,
        });
      }
    } else {
      setProviderServices({
        servicetitle: "",
        servicenumber: "",
        servicechargetype: "",
        servicerate: "",
      });
    }
  };

  const handleService = () => {
    const newService = {
      servicetitle: providerservices.servicetitle,
      servicenumber: providerservices.servicenumber,
      servicechargetype: providerservices.servicechargetype,
      servicerate: providerservices.servicerate,
    };
    setExistingServices([...existingServices, newService]);
    setProviderServices({
      servicetitle: "",
      servicenumber: "",
      servicechargetype: "",
      servicerate: "",
    });
    setSearch("");
  };

  const handleSearchChange = (value) => {
    console.log("value", value);
    setSearch(value);
    debouncedSearch(value);
  };

  const handleServiceCostChange = (name, value) => {
    setProviderServices({ ...providerservices, [name]: value });
  };

  return (
    <Box className={classes.container}>
      {/* Search select */}
      <Select
        value={providerservices.name}
        rightSection={<IconChevronDown display={"none"} color={"var(--app-text)"} size={20} />}
        onChange={(selectedOption) => handleAddService(selectedOption)}
        onSearchChange={(value) => {
          setSearch(value);
          debouncedSearch(value);
        }}
        searchable
        data={
          allproviderservices?.map((item) => ({
            value: item,
            label: `${item.code} - ${item.name}`,
          })) || []
        }
        icon={<IconSearch size={16} color="var(--app-text-muted)" />}
        placeholder="Search service # or Service Title"
        styles={{
          input: {
            borderRadius: "8px",
            border: "1px solid var(--app-border)",
            fontSize: "13px",
            height: "42px",
          },
        }}
      />

      {/* Form fields */}
      <Box className={classes.formGrid2}>
        <Box className={classes.fieldWrap}>
          <Text component="label" className={classes.fieldLabel}>Service Title</Text>
          <CustomTextInput
            name="servicetitle"
            value={providerservices.servicetitle}
            onChange={(e) => handleServiceCostChange("servicetitle", e.target.value)}
            size="sm"
          />
        </Box>

        <Box className={classes.fieldWrap}>
          <Text component="label" className={classes.fieldLabel}>Service Code</Text>
          <CustomTextInput
            name="servicenumber"
            value={providerservices.servicenumber}
            onChange={(e) => handleServiceCostChange("servicenumber", e.target.value)}
            size="sm"
          />
        </Box>

        <Box className={classes.fieldWrap}>
          <Text component="label" className={classes.fieldLabel}>Service Unit</Text>
          <CustomSelectBox
            placeholder="Unit"
            value={providerservices.servicechargetype}
            withAsterisk
            data={[
              { value: "unit", label: "1 Unit" },
              { value: "hour", label: "1 Hour" },
              { value: "day", label: "1 Day" },
              { value: "month", label: "1 Month" },
              { value: "15", label: "15 Minutes" },
              { value: "30", label: "30 Minutes" },
              { value: "45", label: "45 Minutes" },
            ]}
            onChange={(value) => handleServiceCostChange("servicechargetype", value)}
          />
        </Box>
      </Box>

      {/* Add button */}
      <Box
        component="button"
        type="button"
        className={classes.addBtn}
        onClick={handleService}
      >
        + Add code
      </Box>

      {/* Services table */}
      {loading ? (
        <TableLoader />
      ) : (
        (existingServices?.length > 0 ||
          (isEditMode && existingServices?.length > 0)) && (
          <Box className={classes.tableWrap}>
            <table className={classes.table}>
              <thead>
                <tr>
                  <th>Service Name</th>
                  <th>Code</th>
                  <th>Unit Type</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {existingServices?.length > 0 &&
                  existingServices?.map((existingService, index) => (
                    <tr key={index}>
                      <td>{existingService?.servicetitle}</td>
                      <td>{existingService?.servicenumber}</td>
                      <td>
                        <div className={classes.inlineSelectBox}>
                          <Select
                            rightSection={<IconChevronDown color={"var(--app-text)"} size={14} />}
                            rightSectionWidth={20}
                            styles={{ rightSection: { pointerEvents: "none" } }}
                            value={existingService?.servicechargetype || "unit"}
                            onChange={(value) => {
                              setExistingServices((prevState) =>
                                prevState.map((service) =>
                                  service?.servicetitle === existingService?.servicetitle &&
                                  service?.servicenumber === existingService?.servicenumber
                                    ? { ...service, servicechargetype: value }
                                    : service
                                )
                              );
                            }}
                            data={[
                              { value: "unit", label: "1 Unit" },
                              { value: "hour", label: "1 Hour" },
                              { value: "day", label: "1 Day" },
                              { value: "month", label: "1 Month" },
                              { value: "15", label: "15 Minutes" },
                              { value: "30", label: "30 Minutes" },
                              { value: "45", label: "45 Minutes" },
                            ]}
                          />
                        </div>
                      </td>
                      <td>
                        <Box
                          component="button"
                          type="button"
                          className={classes.deleteBtn}
                          onClick={async () => {
                            const willDelete = await swal({
                              title: `Are you sure you want to delete this service?`,
                              text: "This Service Is Used By Other Client",
                              icon: "warning",
                              dangerMode: true,
                              buttons: ["Cancel", "Delete"],
                            });
                            if (willDelete) {
                              let temp = existingServices?.filter((_, i) => i !== index);
                              setExistingServices(temp);
                            }
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                            <path d="M2 4h12M5.333 4V2.667A1.333 1.333 0 016.667 1.333h2.666A1.333 1.333 0 0110.667 2.667V4m2 0v9.333A1.333 1.333 0 0111.333 14.667H4.667A1.333 1.333 0 013.333 13.333V4h9.334z" stroke="currentColor" strokeWidth="1.333" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </Box>
                      </td>
                    </tr>
                  ))}

                {services
                  ?.filter((service) =>
                    isEditMode
                      ? !existingServices.some(
                          (existingService) =>
                            service?.code === existingService?.servicenumber
                        )
                      : true
                  )
                  ?.map((service) => (
                    <tr key={service?._id}>
                      <td>{service?.servicetitle}</td>
                      <td>{service?.servicenumber}</td>
                      {existingServices?.some(
                        (existService) => existService?._id === service?._id
                      ) ? (
                        <td colSpan={2}>
                          <Box
                            component="button"
                            type="button"
                            className={classes.deleteBtn}
                            onClick={async () => {
                              const willDelete = await swal({
                                title: `Are you sure you want to delete this service?`,
                                text: "This Service Is Used By Other Client",
                                icon: "warning",
                                dangerMode: true,
                                buttons: ["Cancel", "Delete"],
                              });
                              if (willDelete) {
                                let temp = existingServices?.filter(
                                  (existService) => existService?._id !== service?._id
                                );
                                setExistingServices(temp);
                              }
                            }}
                          >
                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                              <path d="M2 4h12M5.333 4V2.667A1.333 1.333 0 016.667 1.333h2.666A1.333 1.333 0 0110.667 2.667V4m2 0v9.333A1.333 1.333 0 0111.333 14.667H4.667A1.333 1.333 0 013.333 13.333V4h9.334z" stroke="currentColor" strokeWidth="1.333" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </Box>
                        </td>
                      ) : (
                        <>
                          <td>
                            <div className={classes.inlineSelectBox}>
                              <Select
                                disabled={true}
                                defaultValue={"1unit"}
                                value={service?.servicechargetype}
                                rightSection={<IconChevronDown color={"var(--app-primary)"} size={14} />}
                                styles={{ rightSection: { pointerEvents: "none" } }}
                                onChange={(value) =>
                                  setActiveService((prevState) => ({
                                    ...prevState,
                                    _id: service?._id,
                                    servicetitle: service?.name,
                                    servicenumber: service?.code,
                                    servicechargetype: value,
                                  }))
                                }
                                placeholder="Unit"
                                data={[
                                  { value: "unit", label: "1 Unit" },
                                  { value: "hour", label: "1 Hour" },
                                  { value: "day", label: "1 Day" },
                                  { value: "month", label: "1 Month" },
                                ]}
                              />
                            </div>
                          </td>
                          <td>
                            <Box
                              component="button"
                              type="button"
                              className={classes.deleteBtn}
                              onClick={async () => {
                                const willDelete = await swal({
                                  title: `Are you sure you want to delete this service?`,
                                  text: "This Service Is Used By Other Client",
                                  icon: "warning",
                                  dangerMode: true,
                                  buttons: ["Cancel", "Delete"],
                                });
                                if (willDelete) {
                                  let temp = services?.filter(
                                    (existService) =>
                                      existService?.servicetitle !== service?.servicetitle
                                  );
                                  setServices(temp);
                                }
                              }}
                            >
                              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                                <path d="M2 4h12M5.333 4V2.667A1.333 1.333 0 016.667 1.333h2.666A1.333 1.333 0 0110.667 2.667V4m2 0v9.333A1.333 1.333 0 0111.333 14.667H4.667A1.333 1.333 0 013.333 13.333V4h9.334z" stroke="currentColor" strokeWidth="1.333" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </Box>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
              </tbody>
            </table>
          </Box>
        )
      )}

      <ProviderServicesLookupModal
        openlookup={openlookup}
        setOpenLookup={setOpenLookup}
        setProviderServices={setProviderServices}
      />
    </Box>
  );
}

export default ProviderServiceSearch;
